import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { ColumnDef } from '@tanstack/react-table';

// Actions
import { addPortfolioMetrics } from '@/redux/modules/edit-portfolio';
import { getFundData } from '@/redux/modules/funds/fund-store';
import { getStockData } from '@/redux/modules/stocks/stock-store';

// Components
import DataTableV2 from '@/views/components/data/DataTableV2/DataTableV2';
import EmptySearchResults from '@/views/components/data/DataTableV2/EmptySearchResults';

// Enums
import { PORTFOLIO_METRICS_TABLE_COLUMNS } from '@/enums/Portfolio';

// Types
import Alt from '@/types/models/alt';
import Fund from '@/types/fund';
import Nullable from '@/types/nullable';
import Portfolio from '@/types/portfolio';
import PortfolioHolding from '@/types/portfolio-holding';
import Stock from '@/types/stock';
import User from '@/types/user';
import Locale from '@/types/locale';
import { ReduxState } from '@/redux/modules/types';

// Utils
import useMouseFollowTooltip from '@/hooks/useMouseFollowTooltip';
import { getTableColumns } from './utils/metrics-table-columns-definition';
import { getAltData } from '@/redux/modules/alts/alts-store';
import { Metrics, metricsTableI18nBasePath } from './utils/metrics-table';
import { canViewAlts } from '@/utils/alts';
import { HOLDING_TYPES } from '@/utils/portfolios';

// Hooks
import useScreenSize from '@/hooks/useScreenSize';
import { useMetricsTableColumnVisibility } from './hooks/useMetricsTableColumnVisibility';
import { useMetricsData } from './hooks/useMetricsData';

type DispatchProps = {
  addPortfolioMetrics: (portfolio: Portfolio) => Promise<Portfolio>;
  getFundData: (id: string) => Promise<Fund>;
  getStockData: (id: string) => Promise<Stock>;
  getAltData: (id: string) => Promise<Alt>;
};

type StateProps = {
  editedPortfolio: Portfolio;
  locale: Locale;
  user?: Nullable<User>;
};

type Props = DispatchProps &
  StateProps & {
    portfolioHoldings: PortfolioHolding[];
    portfolioSearchQuery: string;
  };

const PortfolioEditorMetricsTable = ({
  editedPortfolio,
  locale,
  user,
  portfolioHoldings,
  portfolioSearchQuery,
  addPortfolioMetrics,
  getFundData,
  getStockData,
  getAltData,
}: Props) => {
  // For the tooltip on the fixed column, see renderTooltip for more context
  const { setTooltipText, renderTooltip } = useMouseFollowTooltip();

  const hasAltsHoldings = portfolioHoldings.some(holding => holding.original.type === HOLDING_TYPES.ALT);
  const canViewAltsColumns = canViewAlts(user) && hasAltsHoldings;
  const { isDesktop } = useScreenSize();

  // These are the columns to be pinned - calculated directly since it's lightweight
  const initialColumnPinning = isDesktop ? { left: [PORTFOLIO_METRICS_TABLE_COLUMNS.CODE, PORTFOLIO_METRICS_TABLE_COLUMNS.NAME] } : {};

  const columns: ColumnDef<Metrics>[] = useMemo(() => getTableColumns(setTooltipText, locale), [setTooltipText, locale]);

  const columnVisibility = useMetricsTableColumnVisibility(canViewAltsColumns);
  const { metricsData } = useMetricsData({
    editedPortfolio,
    portfolioHoldings,
    addPortfolioMetrics,
    getFundData,
    getStockData,
    getAltData,
  });

  // Empty state component
  const getEmptyDataComponent = () => {
    const i18nEmptyTablePath = `${metricsTableI18nBasePath}.EmptyData.NoResults`;

    return (
      <EmptySearchResults
        searchQuery={portfolioSearchQuery}
        titleToLocalize={`${i18nEmptyTablePath}.Title`}
        messageToLocalize={`${i18nEmptyTablePath}.Message`}
      />
    );
  };

  return (
    <div className="portfolio-editor-metrics-table">
      <div className="left-padding-blind" />
      <DataTableV2
        columnPinningState={initialColumnPinning}
        columnVisibilityState={columnVisibility}
        columns={columns}
        data={metricsData}
        enableColumnFreeze={isDesktop}
        enableSorting
        noDataComponent={getEmptyDataComponent}
        enablePagination
        hidePaginationTotal
      />
      <div className="right-padding-blind" />

      {/* Rendering the name tooltip, this needs to be outside of the table due to overflow issues in fixed columns */}
      {renderTooltip()}
    </div>
  );
};

const mapStateToProps = (state: ReduxState): StateProps => ({
  editedPortfolio: state.editPortfolio.portfolio,
  locale: state.intl.locale,
  user: state.auth.user,
});

const mapDispatchToProps = { addPortfolioMetrics, getFundData, getStockData, getAltData };

export default connect(mapStateToProps, mapDispatchToProps)(PortfolioEditorMetricsTable);
